/*
 * InSight Analytics Solutions Limited
 * https://fieldpro.onyxinsight.com/
 *
 * Copyright (c) 2021 Onyx InSight Field Pro.
 * This file is licensed to InSight Analytics Solutions Limited
 */

import {
  TaskDetails,
  ReportTaskSeverity,
  TaskTopSeverity,
} from '../pages/ReportViewer/interfaces';

function calculateSectionTopSeverity(
  sectionTasks: any[],
): TaskTopSeverity | null {
  let highestDisplaySeq = -1;
  let highestSeverityInfo: ReportTaskSeverity | null = null;
  let severityLookup: ReportTaskSeverity[] = [];

  // For each task in the section
  for (const task of sectionTasks) {
    if (task.taskDetails) {
      // Get the severity lookup table from the first task that has it
      if (!severityLookup.length && task.taskDetails.reportTaskSeverity) {
        severityLookup = task.taskDetails.reportTaskSeverity;
      }

      // Get all observations for this task
      const allObservations = [
        ...(task.taskDetails.reportTaskObservations || []),
        ...(task.taskDetails.reportPhotos?.flatMap(
          (photo: any) => photo.reportPhotoObservations || [],
        ) || []),
      ];

      // For each observation in this task
      for (const observation of allObservations) {
        const severityDisplaySeq =
          (observation as any).severityDisplaySequence ?? 0;

        if (severityDisplaySeq > highestDisplaySeq) {
          highestDisplaySeq = severityDisplaySeq;

          // Find the full severity details from the lookup table
          highestSeverityInfo =
            severityLookup.find(
              severity => severity.displaySeq === severityDisplaySeq,
            ) || null;
        }
      }
    }
  }

  if (!highestSeverityInfo || highestDisplaySeq <= 0) {
    return null;
  }

  // Convert ReportTaskSeverity to TaskTopSeverity format
  return {
    colorBg: highestSeverityInfo.colorBg,
    colorFg: highestSeverityInfo.colorFg,
    displaySeq: highestSeverityInfo.displaySeq,
    hideWhenPrint: highestSeverityInfo.hideWhenPrint,
    nameFull: highestSeverityInfo.nameFull,
    nameShort: highestSeverityInfo.nameShort,
  };
}

/**
 * Recalculates topSeverity for each section based on the highest severity
 * found across ALL observations in ALL tasks within that section.
 *
 * @param sectionData - Array of section data from reportSectionTaskData
 */
export function recalculateTopSeverityForSections(sectionData: any[]): any[] {
  return sectionData.map(section => {
    if (section.reportTemplateSummaryChild) {
      // Calculate the topSeverity for the entire section
      const sectionTopSeverity = calculateSectionTopSeverity(
        section.reportTemplateSummaryChild,
      );

      return {
        ...section,
        topSeverity: sectionTopSeverity,
      };
    }
    return section;
  });
}
